import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Instagram, Linkedin, Mail, Twitter } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Archive', path: '/Archive' },
    { name: 'Testimonials', path: '/testimonials' }
  ];

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/nuviosites' },
    { name: 'LinkedIn', icon: Linkedin, url: 'https://www.linkedin.com/in/nuviosites' },
    { name: 'Twitter', icon: Twitter, url: '#' },
    { name: 'Email', icon: Mail, url: 'mailto:nuviosites@gmail.com' }
  ];

  return (
    <footer className="bg-black text-white relative font-inter">
      {/* 1. Immersive Billboard Section (Shown on all pages) */}
      <section className="px-4 pt-12 pb-8 md:px-12 lg:px-24">
        <motion.div 
          whileHover={{ scale: 0.995 }}
          className="relative h-[450px] md:h-[600px] w-full rounded-[2.5rem] overflow-hidden shadow-2xl group"
        >
          <img
            src="/image.png"
            alt="TDMA Billboard"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          <div className="absolute bottom-10 left-8 md:bottom-16 md:left-16 z-10 max-w-sm">
            <h2 className="text-3xl md:text-5xl font-normal tracking-tighter mb-6 text-white leading-none">
              Start your <br/> next monolith.
            </h2>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full font-bold text-sm hover:bg-[#FF4E45] hover:text-white transition-all transform active:scale-95"
            >
              Get in Touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* 2. Main Footer Content */}
      <div className="px-6 md:px-12 lg:px-24 pt-16 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 border-b border-white/5 pb-20">
          
          {/* Brand Pillar */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block group mb-8">
              <h1 className="text-6xl md:text-8xl font-normal tracking-tight leading-none group-hover:text-[#FF4E45] transition-colors">
                TDMA
              </h1>
            </Link>
            <p className="text-white/40 text-lg md:text-xl font-light max-w-xs leading-relaxed">
              Engineering high-performance digital foundations for visionary brands.
            </p>
          </div>

          {/* Navigation Pillar */}
          <div>
            <h3 className="text-xs font-bold tracking-[0.2em] text-white/20 uppercase mb-8">Navigation</h3>
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link 
                  key={link.name} 
                  to={link.path} 
                  className="text-lg font-light text-white/60 hover:text-white transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/clientportal" className="text-lg font-light text-white/60 hover:text-white transition-colors flex items-center gap-2">
                Client Portal <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded-full text-white/40 font-bold uppercase tracking-widest">New</span>
              </Link>
            </div>
          </div>

          {/* Social / Connect Pillar */}
          <div>
            <h3 className="text-xs font-bold tracking-[0.2em] text-white/20 uppercase mb-8">Connect</h3>
            <div className="flex flex-col gap-6">
              {socialLinks.map((social) => (
                <a 
                  key={social.name} 
                  href={social.url} 
                  className="flex items-center gap-3 text-lg font-light text-white/60 hover:text-white transition-all group"
                >
                  <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-white transition-colors">
                    <social.icon className="w-4 h-4" />
                  </div>
                  {social.name}
                </a>
              ))}
            </div>
          </div>

        </div>

        {/* 3. Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-10 gap-6">
          <div className="text-white/20 text-xs font-medium tracking-widest uppercase">
            © {currentYear} TDMA STUDIO. India.
          </div>
          
          <div className="flex gap-8">
            <a href="#" className="text-white/20 hover:text-white text-xs font-medium tracking-widest uppercase transition-colors">Privacy Policy</a>
            <a href="#" className="text-white/20 hover:text-white text-xs font-medium tracking-widest uppercase transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;